import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Avatar,
  Badge,
  Button,
  Card,
  Heading,
  RoleBadge,
  SectionLabel,
  type BadgeTone,
} from '@openroad/ui';
import { api, formatDay, formatMoney, formatTime, unwrap } from '../lib/api';
import { useEndSessionOnAuthFailure } from '../lib/session';
import { RateTripSheet, type PendingReview } from './RateTripSheet';

/** How the export words each booking state, and in which tone. */
const BOOKING_STATE: Record<string, { label: string; tone: BadgeTone }> = {
  PENDING_APPROVAL: { label: 'Awaiting driver', tone: 'warning' },
  CONFIRMED: { label: 'Confirmed', tone: 'success' },
  DECLINED: { label: 'Declined', tone: 'danger' },
  CANCELLED_BY_PASSENGER: { label: 'You cancelled', tone: 'danger' },
  CANCELLED_BY_DRIVER: { label: 'Driver cancelled', tone: 'danger' },
  COMPLETED: { label: 'Completed', tone: 'neutral' },
};

/**
 * A ride you are driving does not have a booking state — the export shows how
 * full it is instead: "2 seats booked", in the same amber the design uses for
 * anything still waiting on somebody.
 *
 * Booked means confirmed. A request the driver has not answered holds a seat
 * without being a booking, and calling it one here would contradict the same
 * driver's "trips shared" on their profile.
 */
function drivingState(trip: { status: string; seatsConfirmed: number }): {
  label: string;
  tone: BadgeTone;
} {
  if (trip.status === 'CANCELLED') return { label: 'Cancelled', tone: 'danger' };
  if (trip.status === 'COMPLETED') return { label: 'Completed', tone: 'neutral' };

  const booked = trip.seatsConfirmed;
  if (booked === 0) return { label: 'No seats booked', tone: 'neutral' };
  return { label: `${booked} ${booked === 1 ? 'seat' : 'seats'} booked`, tone: 'warning' };
}

interface Row {
  key: string;
  tripId: string;
  origin: string;
  destination: string;
  departureAt: string;
  initials: string;
  avatarColour: string;
  /** The export's third meta field: the driver's name, or "You are driving". */
  who: string;
  role: 'Driver' | 'Passenger';
  state: { label: string; tone: BadgeTone };
  price: string;
}

export function TripsScreen() {
  const navigate = useNavigate();

  // Both sides of the same question — rides you are driving, and seats you have
  // taken. The dashboard the design shows mixes them into one list, so the
  // screen asks for both and splits them by departure itself rather than
  // making four requests and trusting two answers about "now" to agree.
  const driving = useQuery({
    queryKey: ['trips', 'mine'],
    queryFn: () => unwrap(api.GET('/trips/mine', { params: { query: { when: 'all' } } })),
  });

  const riding = useQuery({
    queryKey: ['bookings', 'mine'],
    queryFn: () => unwrap(api.GET('/bookings/mine', { params: { query: { when: 'all' } } })),
  });

  // Finished rides this member still owes a review on, from either seat. This
  // is what makes the export's "Rate trip" button real: a past ride is only
  // rateable when the server says it is.
  const pendingReviews = useQuery({
    queryKey: ['reviews', 'pending'],
    queryFn: () => unwrap(api.GET('/reviews/pending')),
  });

  const [rating, setRating] = useState<PendingReview | null>(null);

  // A 401 on either list means the client's refresh already failed, so the
  // session is gone. Ending it lets RequireMember send them to sign in instead
  // of showing a generic failure they cannot do anything about.
  const expired = useEndSessionOnAuthFailure(driving.error, riding.error, pendingReviews.error);

  const pending = driving.isPending || riding.isPending;
  const failed = driving.isError || riding.isError;

  const rows: Row[] = [
    ...(driving.data?.items ?? []).map((trip) => ({
      key: `trip-${trip.id}`,
      tripId: trip.id,
      origin: trip.origin.name,
      destination: trip.destination.name,
      departureAt: trip.departureAt,
      // /trips/mine only returns rides this member drives, so the driver on
      // them is this member — no need to reach for the session for an avatar
      // the payload already carries.
      initials: trip.driver.initials,
      avatarColour: trip.driver.avatarColour,
      who: 'You are driving',
      role: 'Driver' as const,
      state: drivingState(trip),
      price: formatMoney(trip.pricePerSeat),
    })),
    ...(riding.data?.items ?? []).map((booking) => ({
      key: `booking-${booking.id}`,
      tripId: booking.trip.id,
      origin: booking.trip.origin.name,
      destination: booking.trip.destination.name,
      departureAt: booking.trip.departureAt,
      initials: booking.trip.driver.initials,
      avatarColour: booking.trip.driver.avatarColour,
      who: booking.trip.driver.displayName,
      role: 'Passenger' as const,
      state: BOOKING_STATE[booking.status] ?? {
        label: booking.status,
        tone: 'neutral' as BadgeTone,
      },
      price: formatMoney(booking.price.total),
    })),
  ];

  // Keyed on the trip so a past row can find its own outstanding review.
  const reviewByTrip = new Map(
    (pendingReviews.data?.items ?? []).map((item) => [item.trip.id, item as PendingReview]),
  );

  const now = Date.now();
  const upcoming = rows
    .filter((row) => new Date(row.departureAt).getTime() >= now)
    .sort((a, b) => a.departureAt.localeCompare(b.departureAt));
  const past = rows
    .filter((row) => new Date(row.departureAt).getTime() < now)
    .sort((a, b) => b.departureAt.localeCompare(a.departureAt));

  return (
    <main className="mx-auto max-w-[760px] px-6 pt-7 pb-16">
      <Heading level="page" as="h1" className="mb-6">
        Your trips
      </Heading>

      {pending ? (
        <p className="text-body text-muted">Loading your trips…</p>
      ) : failed ? (
        <Card radius="panel" className="p-6">
          <p className="m-0 text-body text-muted">
            {expired
              ? 'Your session has ended. Signing in again will bring your trips back.'
              : 'We could not load your trips just now. Try again in a moment.'}
          </p>
        </Card>
      ) : (
        <>
          <SectionLabel className="mb-3">Upcoming</SectionLabel>
          <div className="mb-8 flex flex-col gap-3">
            {upcoming.length === 0 ? (
              <Card className="p-5">
                <p className="m-0 text-body text-muted">
                  Nothing booked yet.{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/search')}
                    className="cursor-pointer font-semibold text-ngahere"
                  >
                    Find a ride
                  </button>
                  .
                </p>
              </Card>
            ) : (
              upcoming.map((row) => (
                <UpcomingRow
                  key={row.key}
                  row={row}
                  onOpen={() => navigate(`/trips/${row.tripId}`)}
                />
              ))
            )}
          </div>

          <SectionLabel className="mb-3">Past</SectionLabel>
          <div className="flex flex-col gap-3">
            {past.length === 0 ? (
              <Card className="p-5">
                <p className="m-0 text-body text-muted">No trips behind you yet.</p>
              </Card>
            ) : (
              past.map((row) => (
                <PastRow
                  key={row.key}
                  row={row}
                  pending={reviewByTrip.get(row.tripId) ?? null}
                  onRate={setRating}
                />
              ))
            )}
          </div>
        </>
      )}

      <RateTripSheet pending={rating} onClose={() => setRating(null)} />
    </main>
  );
}

function UpcomingRow({ row, onOpen }: { row: Row; onOpen: () => void }) {
  return (
    <Card lift="ride" onClick={onOpen} className="flex items-center gap-4 p-[18px]">
      <Avatar initials={row.initials} colour={row.avatarColour} size={46} name={row.who} />
      <div className="flex-1">
        <div className="mb-[3px] flex flex-wrap items-center gap-2.5">
          <span className="font-display text-lead font-bold">
            {row.origin} → {row.destination}
          </span>
          <RoleBadge>{row.role}</RoleBadge>
        </div>
        <div className="text-label text-muted">
          {formatDay(row.departureAt)} · {formatTime(row.departureAt)} · {row.who}
        </div>
      </div>
      <div className="text-right">
        <Badge tone={row.state.tone}>{row.state.label}</Badge>
        <div className="mt-1.5 text-label text-subtle">{row.price}</div>
      </div>
    </Card>
  );
}

/**
 * A past trip is a record, not a control: the export draws it without the role
 * badge, without the departure time, and — unlike an upcoming row — without a
 * click.
 *
 * The right-hand slot holds "Rate trip" or "★ Reviewed" in the export. The
 * button is real — `GET /reviews/pending` says which finished rides still owe
 * one. "★ Reviewed" is still not drawn: that endpoint answers what is
 * outstanding, not what has been written, so a row with nothing pending may be
 * one nobody can review rather than one already reviewed. It shows how the trip
 * ended instead. See docs/design-fidelity.md.
 */
function PastRow({
  row,
  pending,
  onRate,
}: {
  row: Row;
  pending: PendingReview | null;
  onRate: (pending: PendingReview) => void;
}) {
  return (
    <Card className="flex items-center gap-4 p-[18px]">
      <Avatar initials={row.initials} colour={row.avatarColour} size={46} faded name={row.who} />
      <div className="flex-1">
        <div className="font-display text-lead font-bold">
          {row.origin} → {row.destination}
        </div>
        <div className="text-label text-muted">
          {formatDay(row.departureAt)} · {row.who}
        </div>
      </div>
      {pending ? (
        <Button variant="ctaSoft" onClick={() => onRate(pending)}>
          Rate trip
        </Button>
      ) : (
        <Badge tone={row.state.tone}>{row.state.label}</Badge>
      )}
    </Card>
  );
}
